import { makeAutoObservable } from 'mobx';
import { PaletteExpansionModel } from './PaletteExpansionModel';
import {
  trackpadGestureMode,
  type TrackpadGestureMode,
} from './grid/trackpadGestureMode';
import type { IKeyMapModel, IUserPreferencesModel } from '@/interfaces/models';
import {
  KeyActions,
  isTileShortcutKey,
  type TileShortcutKey,
  type TileShortcutTarget,
  KeyCombination,
  serializeKeyCombination,
  stringToKeyCombination,
} from '@/models/KeyMapModel';

const TILE_BINDINGS_STORAGE_KEY = 'digitiles-tile-shortcuts';
const SOUND_STORAGE_KEY = 'digitiles-sound';
const KEY_BINDINGS_STORAGE_KEY = 'digitiles-key-bindings';
const RECURSIVE_CHIPS_STORAGE_KEY = 'digitiles-recursive-chips';
const SIDE_BY_SIDE_STORAGE_KEY = 'digitiles-side-by-side-editor';
const CHIP_TABS_STORAGE_KEY = 'digitiles-chip-tabs';
const COMBINED_CHIP_COLORS_STORAGE_KEY = 'digitiles-combined-chip-colors';
const DEBUG_MODE_STORAGE_KEY = 'digitiles-debug-mode';
const VERILOG_EXPORT_STORAGE_KEY = 'digitiles-verilog-export';
const MINIMAP_STORAGE_KEY = 'digitiles-minimap';
const PERFORMANCE_READOUT_STORAGE_KEY = 'digitiles-performance-readout';
const AUTOSAVE_STORAGE_KEY = 'digitiles-autosave';
const BUNDLE_CHIP_IDENTITY_STORAGE_KEY = 'digitiles-bundle-chip-identity';
const RUST_WASM_STORAGE_KEY = 'digitiles-rust-wasm-simulation';
const WORKER_SIMULATION_STORAGE_KEY = 'digitiles-worker-simulation';
const SHOW_GRID_LINES_STORAGE_KEY = 'digitiles-show-grid-lines';
const SHOW_CHIP_OUTLINES_STORAGE_KEY = 'digitiles-show-chip-outlines';
const TRACKPAD_GESTURES_STORAGE_KEY = 'digitiles-experimental-trackpad-gestures';

export class UserPreferencesModel implements IUserPreferencesModel {
  paletteExpansion = new PaletteExpansionModel();
  private customKeyBindings: Map<KeyActions, KeyCombination> = new Map();
  soundConfig = { enabled: false, volume: 0.25 };
  recursiveChipsEnabled = false;
  chipTabsEnabled = false;
  sideBySideEnabled = false;
  combinedChipColorsEnabled = false;
  debugModeEnabled = false;
  verilogExportEnabled = false;
  minimapEnabled = false;
  experimentalTrackpadGestures: TrackpadGestureMode = 'off';
  showGridLines = true;
  showChipOutlines = true;
  performanceReadoutEnabled = false;
  bundleChipIdentityEnabled = false;
  workerSimulationEnabled = false;
  rustWasmSimulationEnabled = false;
  autosaveEnabled = true;
  simulationConfig = {
    maxSyncIterations: 100,
    asyncDelay: 0,
    maximumSpeed: false,
  };

  constructor(public dependencies: { keyMapModel: IKeyMapModel }) {
    try {
      this.sideBySideEnabled = localStorage.getItem(SIDE_BY_SIDE_STORAGE_KEY) === 'true';
    } catch {
      /* Default off. */
    }
    this.loadSoundConfig();
    this.loadKeyBindings();
    this.loadTileShortcuts();
    this.loadSimulationConfig();
    try {
      this.experimentalTrackpadGestures = trackpadGestureMode(
        localStorage.getItem(TRACKPAD_GESTURES_STORAGE_KEY),
      );
    } catch {
      /* Keep this experimental feature disabled when storage is unavailable. */
    }
    try {
      this.recursiveChipsEnabled =
        localStorage.getItem(RECURSIVE_CHIPS_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.chipTabsEnabled = localStorage.getItem(CHIP_TABS_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.debugModeEnabled = localStorage.getItem(DEBUG_MODE_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.verilogExportEnabled =
        localStorage.getItem(VERILOG_EXPORT_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.minimapEnabled = localStorage.getItem(MINIMAP_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.combinedChipColorsEnabled =
        localStorage.getItem(COMBINED_CHIP_COLORS_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.showGridLines = localStorage.getItem(SHOW_GRID_LINES_STORAGE_KEY) !== 'false';
    } catch {
      /* Keep the default. */
    }
    try {
      this.showChipOutlines =
        localStorage.getItem(SHOW_CHIP_OUTLINES_STORAGE_KEY) !== 'false';
    } catch {
      /* Keep the default. */
    }
    try {
      this.performanceReadoutEnabled =
        localStorage.getItem(PERFORMANCE_READOUT_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.workerSimulationEnabled =
        localStorage.getItem(WORKER_SIMULATION_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      this.rustWasmSimulationEnabled =
        localStorage.getItem(RUST_WASM_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    try {
      const stored = localStorage.getItem(AUTOSAVE_STORAGE_KEY);
      this.autosaveEnabled =
        stored === 'true'
          ? true
          : stored === 'false'
            ? false
            : !this.workerSimulationEnabled;
      if (stored !== 'true' && stored !== 'false')
        localStorage.setItem(AUTOSAVE_STORAGE_KEY, String(this.autosaveEnabled));
    } catch {
      this.autosaveEnabled = !this.workerSimulationEnabled;
    }
    try {
      this.bundleChipIdentityEnabled =
        localStorage.getItem(BUNDLE_CHIP_IDENTITY_STORAGE_KEY) === 'true';
    } catch {
      /* Keep the default. */
    }
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setSoundConfig(config: Partial<typeof this.soundConfig>) {
    this.soundConfig = {
      enabled:
        typeof config.enabled === 'boolean' ? config.enabled : this.soundConfig.enabled,
      volume:
        typeof config.volume === 'number' && Number.isFinite(config.volume)
          ? Math.max(0, Math.min(1, config.volume))
          : this.soundConfig.volume,
    };
    try {
      localStorage.setItem(SOUND_STORAGE_KEY, JSON.stringify(this.soundConfig));
    } catch {
      /* Settings still work if storage is unavailable. */
    }
  }

  setRecursiveChipsEnabled(enabled: boolean) {
    this.recursiveChipsEnabled = enabled;
    try {
      localStorage.setItem(RECURSIVE_CHIPS_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setSideBySideEnabled(enabled: boolean) {
    this.sideBySideEnabled = enabled;
    try {
      localStorage.setItem(SIDE_BY_SIDE_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep session choice. */
    }
  }

  setChipTabsEnabled(enabled: boolean) {
    this.chipTabsEnabled = enabled;
    try {
      localStorage.setItem(CHIP_TABS_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setCombinedChipColorsEnabled(enabled: boolean) {
    this.combinedChipColorsEnabled = enabled;
    try {
      localStorage.setItem(COMBINED_CHIP_COLORS_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setDebugModeEnabled(enabled: boolean) {
    this.debugModeEnabled = enabled;
    try {
      localStorage.setItem(DEBUG_MODE_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setVerilogExportEnabled(enabled: boolean) {
    this.verilogExportEnabled = enabled;
    try {
      localStorage.setItem(VERILOG_EXPORT_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setExperimentalTrackpadGestures(mode: TrackpadGestureMode) {
    this.experimentalTrackpadGestures = trackpadGestureMode(mode);
    try {
      localStorage.setItem(
        TRACKPAD_GESTURES_STORAGE_KEY,
        this.experimentalTrackpadGestures,
      );
    } catch {
      /* Keep the in-memory preference. */
    }
  }

  setMinimapEnabled(enabled: boolean) {
    this.minimapEnabled = enabled;
    try {
      localStorage.setItem(MINIMAP_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setPerformanceReadoutEnabled(enabled: boolean) {
    this.performanceReadoutEnabled = enabled;
    try {
      localStorage.setItem(PERFORMANCE_READOUT_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setAutosaveEnabled(enabled: boolean) {
    try {
      localStorage.setItem(AUTOSAVE_STORAGE_KEY, String(enabled));
    } catch {
      throw Error('Unable to remember the autosave setting. It is unchanged.');
    }
    this.autosaveEnabled = enabled;
  }

  setBundleChipIdentityEnabled(enabled: boolean) {
    this.bundleChipIdentityEnabled = enabled;
    try {
      localStorage.setItem(BUNDLE_CHIP_IDENTITY_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setRustWasmSimulationEnabled(enabled: boolean) {
    try {
      localStorage.setItem(RUST_WASM_STORAGE_KEY, String(enabled));
    } catch {
      throw Error('Unable to remember this setting. The simulation mode is unchanged.');
    }
    this.rustWasmSimulationEnabled = enabled;
  }

  setWorkerSimulationEnabled(enabled: boolean) {
    try {
      localStorage.setItem(WORKER_SIMULATION_STORAGE_KEY, String(enabled));
    } catch {
      throw Error('Unable to remember this setting. The simulation mode is unchanged.');
    }
    this.workerSimulationEnabled = enabled;
  }

  setShowGridLines(enabled: boolean) {
    this.showGridLines = enabled;
    try {
      localStorage.setItem(SHOW_GRID_LINES_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  setShowChipOutlines(enabled: boolean) {
    this.showChipOutlines = enabled;
    try {
      localStorage.setItem(SHOW_CHIP_OUTLINES_STORAGE_KEY, String(enabled));
    } catch {
      /* Keep the in-memory setting. */
    }
  }

  private loadSoundConfig() {
    try {
      const stored = JSON.parse(localStorage.getItem(SOUND_STORAGE_KEY) ?? 'null');
      if (stored && typeof stored === 'object') this.setSoundConfig(stored);
    } catch {
      /* Ignore malformed preferences. */
    }
  }

  get keyMapModel() {
    return this.dependencies.keyMapModel;
  }

  setTileShortcut(
    key: TileShortcutKey,
    target: TileShortcutTarget,
    replace = false,
  ): boolean {
    if (!this.keyMapModel.setTileShortcut(key, target, replace)) return false;
    this.saveTileShortcuts();
    return true;
  }

  clearTileShortcut(templateId: string) {
    this.keyMapModel.clearTileShortcut(templateId);
    this.saveTileShortcuts();
  }

  private saveTileShortcuts() {
    try {
      localStorage.setItem(
        TILE_BINDINGS_STORAGE_KEY,
        JSON.stringify(Object.fromEntries(this.keyMapModel.tileBindings)),
      );
    } catch {
      /* Keep session bindings when storage is unavailable. */
    }
  }

  private loadTileShortcuts() {
    try {
      const stored = JSON.parse(localStorage.getItem(TILE_BINDINGS_STORAGE_KEY) ?? '{}');
      if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return;
      for (const [key, target] of Object.entries(stored)) {
        if (!isTileShortcutKey(key) || !target || typeof target !== 'object') continue;
        const { templateId, name } = target as TileShortcutTarget;
        if (typeof templateId !== 'string' || !templateId || typeof name !== 'string')
          continue;
        this.keyMapModel.setTileShortcut(key, { templateId, name });
      }
    } catch {
      /* Ignore malformed or unavailable preferences. */
    }
  }

  setCustomKeyBinding(action: KeyActions, combo: KeyCombination) {
    this.keyMapModel.setChar(action, combo);
    this.customKeyBindings.set(action, combo);
    this.saveKeyBindings();
  }

  getCustomKeyBinding(action: KeyActions): KeyCombination | undefined {
    return this.customKeyBindings.get(action);
  }

  hasCustomKeyBindings(): boolean {
    return this.customKeyBindings.size > 0;
  }

  private saveKeyBindings() {
    const bindings = Object.fromEntries(
      Array.from(this.customKeyBindings.entries()).map(([action, combo]) => [
        action,
        serializeKeyCombination(combo),
      ]),
    );
    localStorage.setItem(KEY_BINDINGS_STORAGE_KEY, JSON.stringify(bindings));
  }

  private loadKeyBindings() {
    try {
      const stored = localStorage.getItem(KEY_BINDINGS_STORAGE_KEY);
      if (stored) {
        const bindings = JSON.parse(stored) as Record<string, string>;
        this.customKeyBindings = new Map();

        // Apply loaded bindings to the key map model
        for (const [actionStr, comboStr] of Object.entries(bindings)) {
          const action = actionStr as KeyActions;
          const combo = stringToKeyCombination(comboStr);
          if (combo) {
            this.customKeyBindings.set(action, combo);
            this.keyMapModel.setChar(action, combo);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load key bindings from localStorage:', error);
    }
  }

  resetKeyBindingsToDefaults() {
    this.customKeyBindings.clear();
    localStorage.removeItem(KEY_BINDINGS_STORAGE_KEY);
    this.keyMapModel.resetToDefaults();
  }

  resetKeyBindingToDefault(action: KeyActions) {
    this.customKeyBindings.delete(action);
    this.saveKeyBindings();
    this.keyMapModel.resetToDefaults();
    // Reapply remaining custom bindings
    for (const [action, combo] of this.customKeyBindings) {
      this.keyMapModel.setChar(action, combo);
    }
  }

  setSimulationConfig(config: Partial<typeof this.simulationConfig>) {
    // Keep the shared observable object: circuits retain this reference.
    const { maximumSpeed, maxSyncIterations, asyncDelay } = config;
    if (maxSyncIterations !== undefined)
      this.simulationConfig.maxSyncIterations = maxSyncIterations;
    if (asyncDelay !== undefined) this.simulationConfig.asyncDelay = asyncDelay;
    if (typeof maximumSpeed === 'boolean') {
      this.simulationConfig.maximumSpeed = maximumSpeed;
    }
    this.saveSimulationConfig();
  }

  private saveSimulationConfig() {
    localStorage.setItem(
      'digitiles-simulation-config',
      JSON.stringify(this.simulationConfig),
    );
  }

  private loadSimulationConfig() {
    try {
      const stored = localStorage.getItem('digitiles-simulation-config');
      if (stored) {
        const config = JSON.parse(stored);
        if (config && typeof config === 'object' && !Array.isArray(config)) {
          this.simulationConfig = {
            maxSyncIterations:
              config.maxSyncIterations ?? this.simulationConfig.maxSyncIterations,
            asyncDelay: config.asyncDelay ?? this.simulationConfig.asyncDelay,
            maximumSpeed: config.maximumSpeed === true,
          };
        }
      }
    } catch (e) {
      console.error('Failed to load simulation config', e);
    }
  }
}
